import * as React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { inputsCustomizations } from './customizations/Inputs';
import { dataDisplayCustomizations } from './customizations/DataDisplay';
import { feedbackCustomizations } from './customizations/Feedback';
import { navigationCustomizations } from './customizations/Navigation';
import { surfacesCustomizations } from './customizations/Surfaces';
import { getDesignTokens } from './ThemePrimitives';
import { datePickersCustomizations } from './customizations/DatePicker';
import { useThemeMode } from '../context/ThemeModeContext';

export default function AppTheme(props) {
  const { children, disableCustomTheme, themeComponents } = props;
  const { mode } = useThemeMode();
  const [themeVersion, setThemeVersion] = React.useState(0);

  React.useEffect(() => {
    const handleThemeChange = () => {
      setThemeVersion(prev => prev + 1);
    };
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-mui-color-scheme', mode);
  }, [mode]);

  const theme = React.useMemo(() => {
    if (disableCustomTheme) return {};

    const designTokens = getDesignTokens(mode);

    return createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      ...designTokens,
      components: {
        ...designTokens.components,
        ...inputsCustomizations,
        ...dataDisplayCustomizations,
        ...feedbackCustomizations,
        ...navigationCustomizations,
        ...surfacesCustomizations,
        ...themeComponents,
        ...datePickersCustomizations,
      },
    });
  }, [disableCustomTheme, themeComponents, themeVersion, mode]);

  if (disableCustomTheme) {
    return <>{children}</>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={(theme) => ({
        ':root': {
          '--scrollbar-thumb': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
          '--scrollbar-thumb-hover': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
          '--primary-main': theme.palette.primary.main,
          '--primary-dark': theme.palette.primary.dark,
          '--secondary-main': theme.palette.secondary.main,
          '--background-default': theme.palette.background.default,
          '--background-paper': theme.palette.background.paper,
          '--text-primary': theme.palette.text.primary,
          '--text-secondary': theme.palette.text.secondary,
        },
        body: {
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          transition: 'background-color 0.2s ease, color 0.2s ease',
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
      })} />
      {children}
    </ThemeProvider>
  );
}
